import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  Check,
  CheckCheck,
  Inbox,
  Pencil,
  SkipForward,
  Trash2,
  Wand2,
} from "lucide-react";
import { MAX_REGENERATIONS_PER_POST, isUnlimited } from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands, useCurrentWorkspace } from "@/lib/queries";
import {
  useApprovals,
  useApproveAll,
  useApprovePost,
  useDeletePost,
  useRevisePost,
  useSkipPost,
  type ApprovalPost,
} from "@/lib/approvals";
import { Badge, Card, EmptyState, Notice, PageHeader, SkeletonRows, Spinner } from "@/components/ui";
import { GenerateImageButton } from "@/components/GenerateImageButton";
import { ErrorBox } from "./LoginPage";

// =============================================================================
// The approvals queue (spec §8).
//
// The primary way posts get approved. WhatsApp is the convenience: it needs a
// verified number, a WABA and an approved template per language, and a post
// must never be unreachable because that side is not set up.
//
// Every post shows its picture, every caption per page, and the time it will
// go out, because approving is a decision about what the audience will see,
// not about a row in a list. Approve, Edit and Skip are the same three actions
// the WhatsApp message offers, and they run the same code underneath.
// =============================================================================

export function ApprovalsPage() {
  const { t } = useTranslation();
  const brands = useBrands();
  // From the plan, so the internal tier's lifted cap is not re-imposed by a
  // disabled button here. The fallback is the shared constant rather than a
  // literal 3: this screen renders before the workspace query resolves.
  const workspace = useCurrentWorkspace();
  const maxRewrites =
    workspace.data?.limits.maxRegenerationsPerPost ?? MAX_REGENERATIONS_PER_POST;
  const [brandId, setBrandId] = useState<string | null>(null);

  const approvals = useApprovals(brandId);
  const approve = useApprovePost();
  const skip = useSkipPost();
  const revise = useRevisePost();
  const approveAll = useApproveAll();
  const remove = useDeletePost();

  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [guidance, setGuidance] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => setError(null), [brandId]);

  const posts = approvals.data ?? [];

  async function run(postId: string, fn: () => Promise<unknown>, fallback: string) {
    setError(null);
    setBusyId(postId);
    try {
      await fn();
    } catch (err) {
      setError(apiErrorMessage(err, fallback));
    } finally {
      setBusyId(null);
    }
  }

  async function onApproveAll() {
    setError(null);
    try {
      // The API refuses an unfiltered approve-all, so "All brands" resolves to
      // each brand in turn rather than silently approving the workspace.
      const targets = brandId ? [brandId] : (brands.data ?? []).map((b) => b.id);
      for (const id of targets) await approveAll.mutateAsync({ brandId: id });
    } catch (err) {
      setError(apiErrorMessage(err, t("approvals.approveFailed")));
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<Inbox size={18} />}
        title={t("approvals.title")}
        subtitle={t("approvals.subtitle")}
        actions={
          <>
            <select
              value={brandId ?? ""}
              onChange={(e) => setBrandId(e.target.value || null)}
              className="select w-full min-w-0 sm:w-auto sm:max-w-xs"
            >
              <option value="">{t("approvals.allBrands")}</option>
              {(brands.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            {posts.length > 1 ? (
              <button
                onClick={() => void onApproveAll()}
                disabled={approveAll.isPending}
                className="btn-secondary btn-sm"
              >
                {approveAll.isPending ? <Spinner /> : <CheckCheck size={15} />}
                {t("approvals.approveAll", { count: posts.length })}
              </button>
            ) : null}
          </>
        }
      />

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      {approvals.isLoading ? (
        <Card>
          <SkeletonRows rows={3} />
        </Card>
      ) : posts.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Check size={20} />}
            title={t("approvals.emptyTitle")}
            description={t("approvals.emptyBody")}
            action={
              <Link to="/photo" className="btn-secondary btn-sm">
                <Wand2 size={14} /> {t("approvals.makeOne")}
              </Link>
            }
          />
        </Card>
      ) : (
        <ul className="space-y-4">
          {posts.map((post) => (
            <li key={post.id}>
              <PostCard
                post={post}
                maxRewrites={maxRewrites}
                busy={busyId === post.id}
                editing={editing === post.id}
                guidance={guidance}
                onGuidance={setGuidance}
                onEdit={() => {
                  setEditing(editing === post.id ? null : post.id);
                  setGuidance("");
                }}
                onApprove={() =>
                  void run(post.id, () => approve.mutateAsync(post.id), t("approvals.approveFailed"))
                }
                onSkip={() =>
                  void run(post.id, () => skip.mutateAsync(post.id), t("approvals.skipFailed"))
                }
                onDelete={() => {
                  if (!window.confirm(t("approvals.confirmDelete"))) return;
                  void run(
                    post.id,
                    () => remove.mutateAsync(post.id),
                    t("approvals.deleteFailed"),
                  );
                }}
                onRevise={() =>
                  void run(
                    post.id,
                    async () => {
                      await revise.mutateAsync({ postId: post.id, guidance });
                      setEditing(null);
                      setGuidance("");
                    },
                    t("approvals.reviseFailed"),
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PostCard({
  post,
  maxRewrites,
  busy,
  editing,
  guidance,
  onGuidance,
  onEdit,
  onApprove,
  onSkip,
  onRevise,
  onDelete,
}: {
  post: ApprovalPost;
  maxRewrites: number;
  busy: boolean;
  editing: boolean;
  guidance: string;
  onGuidance: (v: string) => void;
  onEdit: () => void;
  onApprove: () => void;
  onSkip: () => void;
  onRevise: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const image = post.mediaAsset?.enhancedUrl ?? post.mediaAsset?.originalUrl ?? null;
  const rewritesLeft = maxRewrites - post.regenerationCount;
  const capped = !isUnlimited(maxRewrites);

  const when = post.scheduledAt
    ? new Intl.DateTimeFormat(undefined, {
        timeZone: post.brand.timezone,
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(post.scheduledAt))
    : null;

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-ink">{post.brand.name}</span>
          <Badge tone="neutral">{t(`origins.${post.origin}`, post.origin)}</Badge>
          {post.campaignId ? <Badge tone="info">{t("approvals.fromCampaign")}</Badge> : null}
          {post.monthlyPlanId ? <Badge tone="info">{t("approvals.fromMonth")}</Badge> : null}
        </div>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <CalendarClock size={13} />
          {/* No time yet is normal: approving is what asks the planner for one. */}
          {when ?? t("approvals.noTimeYet")}
        </span>
      </div>

      <div className="grid gap-5 p-5 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
        <div className="min-w-0">
          {image ? (
            <img
              src={image}
              alt=""
              className="w-full rounded-xl border border-border object-cover"
            />
          ) : (
            <div className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border px-3 text-center text-xs text-muted">
              <span>{t("approvals.noImage")}</span>
              {post.status !== "published" ? (
                <GenerateImageButton postId={post.id} variant="block" />
              ) : null}
            </div>
          )}
          {post.topic ? <p className="mt-2 text-xs text-muted">{post.topic}</p> : null}
        </div>

        <div className="min-w-0 space-y-3">
          {post.variants.map((variant) => (
            <div key={variant.id} className="rounded-xl bg-surface p-3">
              <p className="mb-1 break-words text-[11px] font-semibold uppercase tracking-wide text-muted">
                {variant.socialAccount.platform} · {variant.socialAccount.displayName}
              </p>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                {variant.caption}
              </p>
              {variant.hashtags.length > 0 ? (
                <p className="mt-2 break-words text-xs text-primary-700">
                  {variant.hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
                </p>
              ) : null}
            </div>
          ))}

          {editing ? (
            <div className="space-y-2 rounded-xl border border-primary-200 bg-primary-50/50 p-3">
              <label className="block">
                <span className="label">{t("approvals.whatToChange")}</span>
                <textarea
                  value={guidance}
                  rows={2}
                  onChange={(e) => onGuidance(e.target.value)}
                  placeholder={t("approvals.changePlaceholder")}
                  className="input resize-y"
                />
              </label>
              <div className="flex items-center gap-2">
                <button onClick={onRevise} disabled={busy} className="btn-primary btn-sm">
                  {busy ? <Spinner /> : <Wand2 size={14} />}
                  {t("approvals.rewrite")}
                </button>
                <button onClick={onEdit} className="btn-ghost btn-sm">
                  {t("common.cancel")}
                </button>
                {/* No counter where there is no ceiling — "9007199254740991
                    rewrites left" is worse than saying nothing. */}
                {capped ? (
                  <span className="text-xs text-muted">
                    {t("approvals.rewritesLeft", { count: rewritesLeft })}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <button onClick={onApprove} disabled={busy} className="btn-primary btn-sm">
                {busy ? <Spinner /> : <Check size={15} />}
                {t("approvals.approve")}
              </button>
              <button
                onClick={onEdit}
                disabled={busy || (capped && rewritesLeft <= 0)}
                title={capped && rewritesLeft <= 0 ? t("approvals.noRewritesLeft") : undefined}
                className="btn-secondary btn-sm"
              >
                <Pencil size={14} /> {t("approvals.edit")}
              </button>
              <button onClick={onSkip} disabled={busy} className="btn-ghost btn-sm">
                <SkipForward size={14} /> {t("approvals.skip")}
              </button>
              {/* Skip stops a post; delete removes it. Separate because a
                  skipped post stays in the history as a decision, and someone
                  tidying a queue of drafts wants them gone. */}
              <button
                onClick={onDelete}
                disabled={busy}
                title={t("approvals.deleteHint")}
                className="btn-ghost btn-sm ms-auto text-muted hover:text-danger"
              >
                <Trash2 size={14} /> {t("approvals.delete")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
